import { arkScope } from '@/api/workspace/endpoints/arkConfig.ts';
import type { FxOption } from '@/store/option/state.ts';

export const SavedOptionValidator = arkScope.type({
  type: "'Vanilla' | 'Trigger' | 'Binary' | 'Straddle' | 'Strangle' | 'RiskReversal'",
  properties: {
    'currencyPair?': 'string',
    'settlementType?': "'Physical' | 'Cash' | 'Forward' | 'NDF'",
    'cutOffMarketPlace?': 'string',
    'premiumPaymentDateTenor?': 'string',
    premiumType: arkScope
      .type({
        premiumCurrency: 'CurrencyChoice',
        premiumCategory: 'PriceType',
      })
      .default(() => ({ premiumCurrency: 1, premiumCategory: 'AMOUNT' })),
    hedgeType: arkScope.type("'Live' | 'Spot' | 'Forward' | 'Ndf'").default('Live'),

    legs: arkScope
      .type({
        legId: 'string & /^.+\\/\\d+$/',
        'legProductName?': "'Vanilla' | 'Trigger' | 'Binary'",
        'strike?': arkScope
          .type({ type: "'number'", value: 'number' })
          .or({ type: "'delta'", value: 'number' })
          .or({ type: "'shortcut'", value: "'ATM' | 'ATMF' | 'ATMS'" })
          .or({
            type: "'solving'",
            solveValue: 'number',
            way: "'Bid' | 'Ask'",
            solvingSide: arkScope
              .type({ type: "'strategy'" })
              .or({ type: "'compactView'", overrideSolveSide: 'Side' })
              .or({ type: "'riskReversal'", solveSide: "'BuyCall' | 'SellCall'" }),
          }),
        'notional?': 'number',
        'notionalCurrencyIndex?': 'CurrencyChoice',
        'expiryDateTenor?': 'string',
        'type?': '"Call" | "Put" | "None"',
        'side?': 'Side',
      })
      .array(),

    hedges: arkScope
      .type({
        legId: 'string',
        'price?': 'number',
        'notionalAmount?': 'number',
        'notionalCurrency?': 'CurrencyChoice',
      })
      .array(),

    uiState: {
      strategyPanelMode: "'hidden' | 'vertical' | 'horizontal'",
      legsNotionalCurrenciesLinkState: "'linked' | 'unlinked'",
      groupLegsByExpiry: 'boolean',
      expandGreekAndMarketPanels: 'boolean',
    },
  },
  tileId: 'string',
  'clientId?': 'number',
});

export type SavedOption = typeof SavedOptionValidator.infer;

export const SavedOption = {
  fromOption: (option: FxOption, tileId: string): SavedOption => {
    const hedges = option.properties.hedges ?? [];
    return {
      type: option.type,
      properties: {
        currencyPair: option.properties.currencyPair,

        settlementType: option.properties.settlementType,
        cutOffMarketPlace: option.properties.cutOffMarketPlace,
        premiumType: option.properties.premiumType,
        hedgeType: option.properties.hedgeType,

        premiumPaymentDateTenor: option.properties.premiumPaymentDateTenor,

        legs: option.properties.legs.map((leg) => ({
          legId: leg.legId,
          legProductName: leg.legProductName,
          type: leg.type,
          side: leg.side,
          expiryDateTenor: leg.expiryDateTenor,
          strike: leg.strike,
          notional: leg.notional,
          notionalCurrencyIndex: leg.notionalCurrencyIndex,
        })),
        hedges: hedges.map((hedge) => ({
          legId: hedge.legId,
          notionalCurrency: hedge.notionalCurrency,
          notionalAmount: hedge.notionalAmount,
          price: hedge.rate,
        })),
        uiState: option.properties.uiState,
      },
      tileId,
      clientId: option.clientId,
    };
  },
} as const;
